import {
	Button, Input,
	Tab,
	Tabs,
} from "@heroui/react";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import toast from 'react-hot-toast';
import _ from 'lodash';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Dropzone, DropzoneAccept, DropzoneIdle, DropzoneReject} from '@mantine/dropzone';
import PageLoader from '../components/PageLoader.jsx';
import axios from 'axios';
import {getVideoMetadata} from '@remotion/media-utils';

let formData = new FormData();
const UploadVideoModal =  ({onSubmitted, ...props}) => {
	const queryClient = useQueryClient();
	const [deductedToken, setDeductedToken] = useState(0);
	const [videoDuration, setVideoDuration] = useState(0);
	const [fileName, setFileName] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	
	const [tabs, setTabs] = useState('upload')
	const [videoLink, setVideoLink] = useState('')
	const pollRef = useRef({cancelled: false})

	const isValidYoutubeUrl = (url) =>
		/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:[^&]*&)*v=|shorts\/|embed\/|live\/))[A-Za-z0-9_-]{11}/.test(url || '')

	// Stop polling if the modal unmounts (step change / close) so we don't update state on a dead component.
	useEffect(() => () => { pollRef.current.cancelled = true }, [])

	const handleYoutubeSubmit = async () => {
		try {
			setIsLoading(true)
			pollRef.current.cancelled = false

			const fd = new FormData()
			fd.append('video_link', videoLink)
			const start = await repositories.project.startYoutube(fd)
			const videoId = start?.video_id
			if (!videoId) throw new Error('Could not start the download')

			const deadline = Date.now() + 15 * 60 * 1000 // 15 min
			while (!pollRef.current.cancelled && Date.now() < deadline) {
				await new Promise((resolve) => setTimeout(resolve, 3000))
				if (pollRef.current.cancelled) return

				const status = await repositories.project.youtubeStatus(videoId)
				if (status?.status === 'ready') {
					toast.success('Video successfully downloaded')
					if (typeof onSubmitted === 'function') {
						onSubmitted({
							video_title: status?.video_title || 'Untitled project',
							s3_url: status?.file_url,
							video_duration: status?.video_duration || 0,
							token: status?.total_deducted_credit,
							video_link: videoLink,
						})
					}
					setIsLoading(false)
					return
				}
				// 'downloading' → keep polling
			}
			if (!pollRef.current.cancelled) {
				toast.error('Timed out downloading the video. Please try again.')
				setIsLoading(false)
			}
		} catch (error) {
			toast.error(error?.response?.data?.detail || error?.message || 'Something went wrong')
			setIsLoading(false)
		}
	}

	const getFileName = useMemo(() => {
		return formData.get('file_name')
	}, [formData.get('file_name')])
	
	const getMediaData = useMutation({
		mutationKey: ['media_file', getFileName],
		mutationFn: getVideoMetadata,
		onMutate: () => setIsLoading(true),
		onSuccess: (response) => {
			const sizeInMB = _.round(Math.max(formData.get('video_file')?.size, 10) / (1024 * 1024), 2);
			
			setVideoDuration(_.round(response.durationInSeconds, 2));
			generateUrl.mutate({
				file_name: getFileName,
				file_type: formData.get('video_file')?.type || '',
				file_size: sizeInMB,
				video_duration: _.round(response.durationInSeconds, 2),
			})
		
		},
		onError: (error) => {
			toast.error(error?.response?.data?.detail || 'Something went wrong')
			setIsLoading(false)
		}
	})
	
	const generateUrl = useMutation({
		mutationFn: repositories.generatePresignedUrl,
		onSuccess: async (response) => {
			setDeductedToken(response?.total_deducted_credit)
			// file_name is the key the API generated, not the local filename. Carry it
			// through the chain rather than via state, so the value can never go stale.
			await onSubmit.mutate({
				presignedUrl: response?.presigned_url,
				storedFileName: response?.file_name,
			})
		},
		onError: (error) => {
			toast.error(error?.response?.data?.detail || 'Something went wrong')
			setIsLoading(false)
		}
	})
	
	const onSubmit = useMutation({
		mutationFn: ({presignedUrl}) => axios.put(presignedUrl, formData.get('video_file'), {
			headers: {
				'Content-Type': formData.get('video_file')?.type,
			},
		}),
		onSuccess: async (response, {storedFileName}) => {
			toast.success('File successfully uploaded')
			await getUploadedFile.mutate(storedFileName || '')
		},
		onError: (error) => {
			toast.error(error?.response?.data?.detail || 'Something went wrong')
			setIsLoading(false)
		}
	})
	
	const getUploadedFile = useMutation({
		mutationFn: repositories.getUploadedFile,
		onSuccess: async (response, storedFileName) => {
			if (typeof onSubmitted === 'function') {
				const data = {
					project_name: formData.get('file_name'),
					token: deductedToken,
					video_duration: videoDuration,
					// The object is stored under the key the API generated, so the
					// original name - which may contain #, ? or spaces - is only ever
					// used for display, above.
					s3_url: storedFileName,
				}
				onSubmitted(data)
			}
			setIsLoading(false)
		},
		onError: (error) => {
			toast.error(error?.response?.data?.detail || 'Something went wrong')
			setIsLoading(false)
		}
	})
	
	return (
		<>
			<div className="flex w-full flex-col">
				<Tabs
					selected={tabs}
					onSelectionChange={(tab) => setTabs(tab)}
					classNames={{
						tabList: 'w-full rounded-2xl p-1.5 bg-[#fcfcfd] border border-[#12131714]',
						tab: 'min-h-11 rounded-xl',
						cursor: '!bg-[#7659fa] shadow-sm',
						tabContent: 'group-data-[selected=true]:text-white text-slate-500',
					}}
					aria-label="Options">
					<Tab
						key="upload"
						title={
							<div className="flex items-center space-x-2">
								<i className="hgi-stroke hgi-folder-video text-xl"></i>
								<span>Upload video</span>
							</div>
						}>
						<div className="min-h-72 mt-3 h-full flex">
							<div className="text-center grow h-[initial] border-dashed border-2 overflow-hidden border-[#7659fa33] bg-[#fcfcfd] rounded-2xl cursor-pointer flex flex-col items-center justify-center transition hover:border-[#7659fa66]">
								<Dropzone
									classNames={{
										root: 'w-full h-full',
										inner: 'w-full h-full flex items-center'
									}}
									accept={['video/mp4', 'video/quicktime', 'video/x-matroska']}
									onDrop={([file]) => {
										formData = new FormData();
										formData.append('file_name', file?.name)
										formData.append('video_file', file)
										setFileName(file?.name)
									}}>
									<DropzoneAccept>
										<div className="w-full bg-green-50 h-full rounded-2xl">
											<PageLoader/>
										</div>
									</DropzoneAccept>
									<DropzoneReject>
										<div className="w-full">
											<i className="hgi-stroke hgi-video-off text-red-500 text-6xl max-w-24 mx-auto mb-5"></i>

											<h2 className="font-sans font-bold text-lg">
												This file type is not supported, Please upload a valid file.
											</h2>
											<p className="text-sm text-slate-500 mt-2 mb-5">MP4, MOV, MKV</p>
										</div>
									</DropzoneReject>
									<DropzoneIdle>
										{_.isEmpty(fileName) ?
											<div className="w-full">
												<div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-[#7659fa1a] text-[#7659fa]">
													<i className="hgi-stroke hgi-inbox-upload text-3xl"></i>
												</div>
												<h2 className="font-sans font-bold text-lg text-slate-700">Upload file, drag or drop here</h2>
												<p className="text-xs text-slate-400 mt-1">MP4, MOV, MKV</p>
											</div>
											:
											<div className="w-full">
												<div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-[#7659fa1a] text-[#7659fa]">
													<i className="hgi-stroke hgi-computer-video text-3xl"></i>
												</div>
												<h2 className="font-sans font-bold text-lg text-slate-700">Uploaded file</h2>
												<p className="text-sm text-slate-500 mt-1 break-all px-6">{fileName}</p>
											</div>
										}
									</DropzoneIdle>
								</Dropzone>
							</div>
						</div>
					</Tab>
					<Tab
						key="url"
						title={
							<div className="flex items-center space-x-2">
								<i className="hgi-stroke hgi-youtube text-xl"></i>
								<span>Youtube URL</span>
							</div>
						}>
						<div className="min-h-72 mt-3 flex">
							<div className="w-full h-fit">
								<Input
									labelPlacement="outside"
									placeholder="https://www.youtube.com/watch?v=N-nx9aMNnM99"
									type="text"
									value={videoLink}
									onValueChange={setVideoLink}
									classNames={{
										inputWrapper: 'min-h-14 bg-[#fcfcfd] rounded-xl border border-[#12131714] focus-within:!border-[#7659fa]',
										input: '!text-sm text-slate-700 placeholder:text-gray-400',
									}}
								/>
							</div>
						</div>
					</Tab>
				</Tabs>
			</div>
			<div className="flex justify-end mt-5">
				<Button
					isDisabled={tabs === 'url' ? !isValidYoutubeUrl(videoLink) : _.isEmpty(fileName)} isLoading={isLoading}
					onPress={() => {
						if (tabs === 'url') {
							handleYoutubeSubmit()
							return
						}
						const blobUrl = URL.createObjectURL(formData.get('video_file'));
						console.log(blobUrl)
						getMediaData.mutate(blobUrl)
					}}
					className={`h-11 rounded-xl px-6 text-sm font-medium text-white transition ${(tabs === 'url' ? !isValidYoutubeUrl(videoLink) : _.isEmpty(fileName)) ? 'bg-[#b5a7f2] cursor-not-allowed' : 'bg-[#7659fa] shadow-lg shadow-[#7659fa40] hover:opacity-95'}`}>
					<i className="hgi-stroke hgi-upload-04 text-[15px]"></i> {tabs === 'url' ? 'Import video' : 'Upload video'}
				</Button>
			</div>
		</>
	)
};

export default UploadVideoModal;
